/**
 * List of the top critical/high severity findings on the dashboard.
 */

import { useNavigate } from 'react-router-dom';
import SeverityBadge from '@/components/common/SeverityBadge';
import { formatCvss } from '@/utils/formatters';
import type { Finding } from '@/types/finding';

interface TopVulnerabilitiesProps {
  findings: Finding[];
}

export default function TopVulnerabilities({ findings }: TopVulnerabilitiesProps) {
  const navigate = useNavigate();

  // Show top 5 critical and high findings.
  const top = findings
    .filter((f) => f.severity === 'critical' || f.severity === 'high')
    .slice(0, 5);

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Top Vulnerabilities</h3>
        <button
          onClick={() => navigate('/findings')}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          View all
        </button>
      </div>

      <div className="space-y-3">
        {top.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">No critical or high findings.</p>
        ) : (
          top.map((finding) => (
            <div
              key={finding.id}
              className="flex items-center justify-between rounded-md border px-3 py-2 hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate('/findings')}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{finding.title}</p>
                <p className="text-xs text-gray-400 font-mono truncate">
                  {finding.asset?.label ?? finding.asset?.value ?? '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {finding.cve_id ?? 'No CVE'} | CVSS: {formatCvss(finding.cvss_score)}
                </p>
              </div>
              <SeverityBadge severity={finding.severity} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
